import { useState } from 'react'
import type { FormEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Card, CardBody, CardHeader } from '@/components/ui/Card'
import { useToast } from '@/components/ui/Toast'
import { Badge } from '@/components/ui/Badge'
import { SkeletonRows } from '@/components/ui/Skeleton'
import { EmptyState } from '@/components/ui/EmptyState'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Select } from '@/components/ui/Select'
import { Sheet } from '@/components/ui/Sheet'
import { useProfile } from '@/auth/SessionProvider'
import { ROLE_LABELS, createPerson, grantRole, listAllDepartments, listPeople, resetPassword, revokeRole, setPersonActive } from '@/data/admin'
import type { Person, Role } from '@/data/admin'
import { personFormSchema } from './schemas'

const errText = (e: unknown) => (e instanceof Error ? e.message : 'Something went wrong. Please try again.')
const ROLES = Object.keys(ROLE_LABELS) as Role[]
const needsDept = (r: Role) => r === 'dept_treasurer' || r === 'dept_viewer'
const linkBtn = 'inline-flex min-h-[44px] shrink-0 items-center rounded-control px-3 text-body font-medium hover:bg-neutral-tint'

/** Central treasury: who has an account, what each person may do, and the temporary passwords. */
export function PeoplePage() {
  const me = useProfile()
  const canAct = me.isAdmin
  const qc = useQueryClient()
  const peopleQ = useQuery({ queryKey: ['people'], queryFn: listPeople })
  const deptsQ = useQuery({ queryKey: ['all-departments'], queryFn: listAllDepartments })
  const depts = (deptsQ.data ?? []).filter((d) => !d.isCentral && !d.archivedAt)
  const deptName = (id: string | null) => deptsQ.data?.find((d) => d.id === id)?.name ?? 'Unknown department'

  const toast = useToast()
  const [actionError, setActionError] = useState('')
  const refresh = async () => {
    await qc.invalidateQueries({ queryKey: ['people'] })
  }

  // What to hand over: a temporary password is shown ONCE and never stored here.
  const [secret, setSecret] = useState<{ who: string; password: string } | null>(null)
  const [copied, setCopied] = useState(false)
  const [leaving, setLeaving] = useState(false)
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(secret?.password ?? '')
      setCopied(true)
    } catch {
      setCopied(false)
    }
  }

  // ---------------- add a person
  const [addOpen, setAddOpen] = useState(false)
  const [fName, setFName] = useState('')
  const [fEmail, setFEmail] = useState('')
  const [fPhone, setFPhone] = useState('')
  const [fRole, setFRole] = useState<Role>('dept_treasurer')
  const [fDept, setFDept] = useState('')
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [formError, setFormError] = useState('')
  const createM = useMutation({ mutationFn: createPerson })

  const openAdd = () => {
    setFName(''); setFEmail(''); setFPhone(''); setFRole('dept_treasurer'); setFDept(''); setErrors({}); setFormError(''); setAddOpen(true)
  }
  const submitAdd = async (e: FormEvent) => {
    e.preventDefault()
    if (createM.isPending) return
    setFormError('')
    const p = personFormSchema.safeParse({ fullName: fName, email: fEmail, phone: fPhone, role: fRole, departmentId: fDept })
    if (!p.success) {
      const errs: Record<string, string> = {}
      for (const i of p.error.issues) errs[String(i.path[0])] ??= i.message
      return setErrors(errs)
    }
    setErrors({})
    const v = p.data
    try {
      const r = await createM.mutateAsync({ email: v.email, fullName: v.fullName, phone: v.phone || undefined, role: v.role, departmentId: needsDept(v.role) ? v.departmentId : null })
      await refresh()
      setAddOpen(false)
      setCopied(false)
      setLeaving(false)
      setSecret({ who: v.fullName, password: r.temp_password })
      createM.reset() // do not keep the password in memory longer than the screen needs it
      toast.success(`${v.fullName} now has an account.`)
    } catch (err) {
      setFormError(errText(err))
    }
  }

  // ---------------- reset a password
  const [resetFor, setResetFor] = useState<Person | null>(null)
  const resetM = useMutation({ mutationFn: resetPassword })
  const confirmReset = async () => {
    if (!resetFor || resetM.isPending) return
    setActionError('')
    try {
      const r = await resetM.mutateAsync(resetFor.id)
      await refresh()
      setCopied(false)
      setLeaving(false)
      setSecret({ who: resetFor.fullName, password: r.temp_password })
      resetM.reset()
      setResetFor(null)
    } catch (err) {
      setActionError(errText(err))
    }
  }

  // ---------------- roles
  const [roleFor, setRoleFor] = useState<Person | null>(null)
  const [gRole, setGRole] = useState<Role>('dept_treasurer')
  const [gDept, setGDept] = useState('')
  const [gError, setGError] = useState('')
  const grantM = useMutation({ mutationFn: (v: { id: string; role: Role; dept: string | null }) => grantRole(v.id, v.role, v.dept) })
  const submitGrant = async (e: FormEvent) => {
    e.preventDefault()
    if (!roleFor || grantM.isPending) return
    if (needsDept(gRole) && gDept === '') return setGError('Pick the department')
    if (roleFor.roles.some((r) => r.role === gRole && (r.departmentId ?? '') === (needsDept(gRole) ? gDept : ''))) return setGError('They already have that role.')
    setGError('')
    try {
      await grantM.mutateAsync({ id: roleFor.id, role: gRole, dept: needsDept(gRole) ? gDept : null })
      await refresh()
      setRoleFor(null)
      toast.success(`${roleFor.fullName} was given the role.`)
    } catch (err) {
      setGError(errText(err))
    }
  }

  const [revoke, setRevoke] = useState<{ person: Person; roleId: string; label: string } | null>(null)
  const revokeM = useMutation({ mutationFn: revokeRole })
  const confirmRevoke = async () => {
    if (!revoke || revokeM.isPending) return
    setActionError('')
    try {
      await revokeM.mutateAsync(revoke.roleId)
      await refresh()
      toast.success(`${revoke.person.fullName} no longer has that role.`)
      setRevoke(null)
    } catch (err) {
      setActionError(errText(err))
    }
  }

  // ---------------- switch on / off
  const [toggle, setToggle] = useState<Person | null>(null)
  const toggleM = useMutation({ mutationFn: (v: { id: string; active: boolean }) => setPersonActive(v.id, v.active) })
  const confirmToggle = async () => {
    if (!toggle || toggleM.isPending) return
    setActionError('')
    try {
      await toggleM.mutateAsync({ id: toggle.id, active: !toggle.isActive })
      await refresh()
      toast.success(toggle.isActive ? `${toggle.fullName}'s account is switched off. They cannot sign in.` : `${toggle.fullName}'s account is switched on.`)
      setToggle(null)
    } catch (err) {
      setActionError(errText(err))
    }
  }

  const people = peopleQ.data ?? []
  const roleText = (r: Person['roles'][number]) => `${ROLE_LABELS[r.role].split(' (')[0]}${r.departmentId ? `, ${deptName(r.departmentId)}` : ''}`

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-title font-semibold text-ink">People</h2>
          <p className="mt-1 text-muted">{canAct ? 'Everyone with an account, and what each person may do.' : 'Everyone with an account. You can look but not change it.'}</p>
        </div>
        {canAct && <Button size="lg" onClick={openAdd}>Add a person</Button>}
      </div>
      {actionError && !resetFor && !revoke && !toggle && <p role="alert" className="rounded-control bg-bad-tint px-4 py-3 text-body text-bad">{actionError}</p>}

      <Card>
        <CardHeader><h3 className="font-semibold text-ink">Accounts</h3></CardHeader>
        <CardBody>
          {peopleQ.isPending ? (
            <SkeletonRows rows={4} />
          ) : peopleQ.isError ? (
            <div>
              <p role="alert" className="text-body text-bad">{errText(peopleQ.error)}</p>
              <Button variant="secondary" size="lg" className="mt-3" onClick={() => void peopleQ.refetch()}>Try again</Button>
            </div>
          ) : people.length === 0 ? (
            <EmptyState title="No accounts yet" />
          ) : (
            <ul className="divide-y divide-line">
              {people.map((p) => (
                <li key={p.id} className="py-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="font-medium text-ink">{p.fullName}</p>
                      <p className="break-all text-body text-muted">{p.email}{p.phone ? ` · ${p.phone}` : ''}</p>
                      <p className="mt-1 flex flex-wrap gap-1">
                        {p.roles.length === 0 && <Badge tone="warning">No role</Badge>}
                        {p.roles.map((r) => (
                          <Badge key={r.id} tone="info">
                            {roleText(r)}
                            {canAct && !(p.id === me.id && r.role === 'admin') && !(p.isOwner && p.id !== me.id) && (
                              <button type="button" aria-label={`Remove the role ${roleText(r)} from ${p.fullName}`} className="ml-1 font-bold" onClick={() => { setActionError(''); setRevoke({ person: p, roleId: r.id, label: roleText(r) }) }}>
                                ×
                              </button>
                            )}
                          </Badge>
                        ))}
                        {!p.isActive && <Badge tone="danger">Switched off</Badge>}
                        {p.mustChangePassword && <Badge tone="neutral">Has not signed in yet</Badge>}
                      </p>
                    </div>
                  </div>
                  {canAct && (
                    <div className="flex flex-wrap gap-1">
                      <button type="button" className={`${linkBtn} text-brand`} onClick={() => { setGError(''); setGRole('dept_treasurer'); setGDept(''); setRoleFor(p) }}>Add a role</button>
                      {!(p.isOwner && p.id !== me.id) && (
                        <button type="button" className={`${linkBtn} text-brand`} onClick={() => { setActionError(''); setResetFor(p) }}>Reset password</button>
                      )}
                      {p.id === me.id ? (
                        <span className="inline-flex min-h-[44px] items-center px-3 text-micro text-muted">This is you: another administrator must switch your account off</span>
                      ) : p.isOwner ? (
                        <>
                          <span className="inline-flex min-h-[44px] items-center px-3 text-micro text-muted">The owner's account: only the owner can change it, except to switch it off in an emergency</span>
                          {p.isActive && (
                            <button type="button" className={`${linkBtn} text-bad`} onClick={() => { setActionError(''); setToggle(p) }}>Switch off</button>
                          )}
                        </>
                      ) : (
                        <button type="button" className={`${linkBtn} ${p.isActive ? 'text-bad' : 'text-brand'}`} onClick={() => { setActionError(''); setToggle(p) }}>{p.isActive ? 'Switch off' : 'Switch on'}</button>
                      )}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </CardBody>
      </Card>

      <Sheet isOpen={addOpen} onClose={() => setAddOpen(false)} title="Add a person">
        <form onSubmit={(e) => void submitAdd(e)} className="flex flex-col gap-4" noValidate>
          <Input label="Full name" value={fName} onChange={(e) => setFName(e.target.value)} error={errors.fullName} />
          <Input label="Email address" type="email" value={fEmail} onChange={(e) => setFEmail(e.target.value)} error={errors.email} hint="They sign in with this." />
          <Input label="Phone (optional)" inputMode="tel" placeholder="+260971234567" value={fPhone} onChange={(e) => setFPhone(e.target.value)} error={errors.phone} />
          <Select label="What may they do?" value={fRole} onChange={(e) => setFRole(e.target.value as Role)} options={ROLES.map((r) => ({ value: r, label: ROLE_LABELS[r] }))} />
          {needsDept(fRole) && (
            <Select label="Which department?" value={fDept} onChange={(e) => setFDept(e.target.value)} options={[{ value: '', label: 'Choose a department' }, ...depts.map((d) => ({ value: d.id, label: d.name }))]} error={errors.departmentId} />
          )}
          {formError && <p role="alert" className="text-body text-bad">{formError}</p>}
          <Button type="submit" size="lg" isLoading={createM.isPending}>{createM.isPending ? 'Creating...' : 'Create account'}</Button>
        </form>
      </Sheet>

      <Sheet isOpen={secret !== null} onClose={() => setLeaving(true)} title="Give them this password">
        <div className="flex flex-col gap-4">
          <p className="text-neutral">Tell {secret?.who} this temporary password in person or by message. <strong>It is shown only now</strong> and cannot be looked up later. They will be asked to choose their own at first sign-in.</p>
          <p className="select-all rounded-control bg-neutral-tint px-4 py-4 text-center font-mono text-title tracking-wide text-ink" aria-label="Temporary password">{secret?.password}</p>
          <Button size="lg" variant="secondary" onClick={() => void copy()}>{copied ? 'Copied' : 'Copy password'}</Button>
          {leaving && (
            <p role="alert" className="rounded-control bg-wait-tint px-4 py-3 text-body text-wait">
              Once you close this the password cannot be shown again. If you have not passed it on, you will need to reset it.
            </p>
          )}
          <Button size="lg" onClick={() => { setSecret(null); setLeaving(false) }}>I have passed it on</Button>
          {leaving && <Button size="lg" variant="secondary" onClick={() => setLeaving(false)}>Keep it open</Button>}
        </div>
      </Sheet>

      <Sheet isOpen={resetFor !== null} onClose={() => setResetFor(null)} title="Reset this password?">
        <div className="flex flex-col gap-4">
          <p className="text-neutral">{resetFor?.fullName} will not be able to sign in with their old password. You will be shown a new temporary one to give them.</p>
          {actionError && <p role="alert" className="text-body text-bad">{actionError}</p>}
          <Button size="lg" variant="danger" isLoading={resetM.isPending} onClick={() => void confirmReset()}>Reset password</Button>
          <Button size="lg" variant="secondary" onClick={() => setResetFor(null)}>Cancel</Button>
        </div>
      </Sheet>

      <Sheet isOpen={roleFor !== null} onClose={() => setRoleFor(null)} title={`Add a role for ${roleFor?.fullName ?? ''}`}>
        <form onSubmit={(e) => void submitGrant(e)} className="flex flex-col gap-4" noValidate>
          <Select label="Role" value={gRole} onChange={(e) => setGRole(e.target.value as Role)} options={ROLES.map((r) => ({ value: r, label: ROLE_LABELS[r] }))} />
          {needsDept(gRole) && <Select label="Which department?" value={gDept} onChange={(e) => setGDept(e.target.value)} options={[{ value: '', label: 'Choose a department' }, ...depts.map((d) => ({ value: d.id, label: d.name }))]} />}
          {gError && <p role="alert" className="text-body text-bad">{gError}</p>}
          <Button type="submit" size="lg" isLoading={grantM.isPending}>Add role</Button>
        </form>
      </Sheet>

      <Sheet isOpen={revoke !== null} onClose={() => setRevoke(null)} title="Remove this role?">
        <div className="flex flex-col gap-4">
          <p className="text-neutral">{revoke?.person.fullName} will no longer be: {revoke?.label}. The record of what they did stays.</p>
          {actionError && <p role="alert" className="text-body text-bad">{actionError}</p>}
          <Button size="lg" variant="danger" isLoading={revokeM.isPending} onClick={() => void confirmRevoke()}>Remove role</Button>
          <Button size="lg" variant="secondary" onClick={() => setRevoke(null)}>Keep it</Button>
        </div>
      </Sheet>

      <Sheet isOpen={toggle !== null} onClose={() => setToggle(null)} title={toggle?.isActive ? 'Switch this account off?' : 'Switch this account on?'}>
        <div className="flex flex-col gap-4">
          <p className="text-neutral">
            {toggle?.isActive
              ? `${toggle.fullName} will not be able to sign in. Nothing they recorded is lost: it belongs to the department.`
              : `${toggle?.fullName} will be able to sign in again.`}
          </p>
          {actionError && <p role="alert" className="text-body text-bad">{actionError}</p>}
          <Button size="lg" variant={toggle?.isActive ? 'danger' : 'primary'} isLoading={toggleM.isPending} onClick={() => void confirmToggle()}>{toggle?.isActive ? 'Switch off' : 'Switch on'}</Button>
          <Button size="lg" variant="secondary" onClick={() => setToggle(null)}>Cancel</Button>
        </div>
      </Sheet>
    </div>
  )
}
